import type { RaFindingRequestState, RaFindingStatus } from '@felix/domain';
import type { StatusTone } from '../../../StatusBadge.js';
import type { RaFindingAction, RaFindingActionInput, RaFindingSummary } from './types.js';

/**
 * Findings & Actions ekranının saf yardımcıları (K6; ADR 0094): yol, süzgeç, sıra, durum tonu ve
 * faaliyet taslağı. Kural kararları domain'dedir (`raFindingState`, `raFindingCloseBlockers`).
 */

/** Compliance ▸ Risk Assessment ▸ Findings & Actions. */
export const RA_FINDINGS_PATH = '/compliance/risk-assessment/findings';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type RaFindingsRoute =
  | { view: 'list' }
  | { view: 'detail'; id: string }
  | { view: 'new'; verificationId: string };

/** Görünüm sorgu dizesinden: `?id=…` ya da `?new=<doğrulama>`. */
export function raFindingsRoute(search: string): RaFindingsRoute {
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (id && UUID.test(id)) return { view: 'detail', id };
  const verification = params.get('new');
  if (verification && UUID.test(verification)) return { view: 'new', verificationId: verification };
  return { view: 'list' };
}

export function raFindingsPath(route: RaFindingsRoute): string {
  switch (route.view) {
    case 'list':
      return RA_FINDINGS_PATH;
    case 'detail':
      return `${RA_FINDINGS_PATH}?id=${route.id}`;
    case 'new':
      return `${RA_FINDINGS_PATH}?new=${route.verificationId}`;
  }
}

export const FINDING_FILTERS = ['all', 'open', 'blocking', 'overdue', 'company', 'closed'] as const;
export type FindingFilter = (typeof FINDING_FILTERS)[number];

export interface FindingListFilter {
  query: string;
  state: FindingFilter;
}
export const EMPTY_FINDING_FILTER: FindingListFilter = { query: '', state: 'all' };

const OPEN: readonly RaFindingStatus[] = ['open', 'action_in_progress', 'corrected', 'reverified'];
const isOpen = (f: RaFindingSummary) => OPEN.includes(f.status);
/** Şirketten yanıt bekleyen: yükseltme ya da değişiklik talebi. */
const awaitingCompany = (f: RaFindingSummary) =>
  f.escalation === 'pending' || f.changeRequest === 'pending';

const norm = (value: string | null | undefined) => (value ?? '').toLocaleLowerCase();

function matches(f: RaFindingSummary, state: FindingFilter): boolean {
  switch (state) {
    case 'all':
      return true;
    case 'open':
      return isOpen(f);
    case 'blocking':
      return isOpen(f) && f.blocking;
    case 'overdue':
      return f.overdue;
    case 'company':
      return awaitingCompany(f);
    case 'closed':
      return !isOpen(f);
  }
}

/** Sıra: açık engelleyici başta, sonra açık, kapanmış ve iptal; sonra yeniden eskiye. */
function rank(f: RaFindingSummary): number {
  if (isOpen(f)) return f.blocking ? 0 : 1;
  return f.status === 'closed' ? 2 : 3;
}

export function filterFindings(
  items: readonly RaFindingSummary[],
  filter: FindingListFilter,
): RaFindingSummary[] {
  const q = norm(filter.query.trim());
  return items
    .filter(
      (f) =>
        matches(f, filter.state) &&
        (q === '' ||
          norm(f.number).includes(q) ||
          norm(f.verificationNo).includes(q) ||
          norm(f.operation).includes(q) ||
          norm(f.description).includes(q) ||
          norm(f.responsible).includes(q)),
    )
    .sort((a, b) => rank(a) - rank(b) || b.createdAt.localeCompare(a.createdAt));
}

/** Özet sayıları (kartlar). */
export function findingStats(items: readonly RaFindingSummary[]) {
  return {
    open: items.filter(isOpen).length,
    blocking: items.filter((f) => isOpen(f) && f.blocking).length,
    overdue: items.filter((f) => f.overdue).length,
    company: items.filter(awaitingCompany).length,
  };
}

export function findingTone(status: RaFindingStatus): StatusTone {
  switch (status) {
    case 'open':
      return 'danger';
    case 'action_in_progress':
      return 'warning';
    case 'corrected':
    case 'reverified':
      return 'info';
    case 'closed':
      return 'success';
    case 'cancelled':
      return 'neutral';
  }
}

export function requestTone(state: RaFindingRequestState): StatusTone {
  switch (state) {
    case 'pending':
      return 'warning';
    case 'approved':
    case 'accepted':
      return 'success';
    case 'returned':
    case 'rejected':
      return 'danger';
    case 'none':
      return 'neutral';
  }
}

/** Faaliyetin düzenleme taslağı. */
export function actionDraft(action: RaFindingAction): RaFindingActionInput {
  return {
    id: action.id,
    description: action.description,
    responsible: action.responsible,
    dueOn: action.dueOn,
    status: action.status,
    completedOn: action.completedOn,
    evidence: action.evidence,
    cancelReason: action.cancelReason,
  };
}

export const EMPTY_ACTION: RaFindingActionInput = {
  id: null,
  description: '',
  responsible: null,
  dueOn: null,
  status: 'open',
  completedOn: null,
  evidence: null,
  cancelReason: null,
};

/** Taslağın eksikleri (sunucu aynısını ayrıca denetler): açıklama, kanıt ve gün, iptal gerekçesi. */
export function actionIssues(action: RaFindingActionInput): string[] {
  const issues: string[] = [];
  const blank = (v: string | null | undefined) => (v ?? '').trim() === '';
  if (blank(action.description)) issues.push('ACTION_DESCRIPTION_REQUIRED');
  if (action.status === 'completed') {
    if (blank(action.evidence)) issues.push('EVIDENCE_REQUIRED');
    if (blank(action.completedOn)) issues.push('COMPLETED_DATE_REQUIRED');
  }
  if (action.status === 'cancelled' && blank(action.cancelReason)) {
    issues.push('CANCEL_REASON_REQUIRED');
  }
  return issues;
}
